import React, { useCallback, useEffect, useState } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import { getTool, invokeTool, InvokeOutcome, Tool, ToolStatus } from '../../api/toolbox';
import { Device, deviceLabel, listDevices, updateDevice } from '../../api/device';
import { alertApprovalIfPending, ApiError } from '../../api/request';
import PowerCalcWidget from '../device/PowerCalcWidget';
import SearchSelect from '../../components/SearchSelect';
import { confirmDialog, alertDialog } from '../../utils/dialog';
import { useCan } from '../../store';

// 工具二级页. 路由: /tools/:toolKey
// 与原型 toolView() 对齐: 根据 toolKey 分支显示具体功能:
// - power-calc → 复用 PowerCalcWidget (% ↔ W 双向插值)
// - 其他 → 工具元信息 + 统一 Tool API 试跑 (§7.8: 平台按注册表透传到工具服务)

const STATUS_TAG: Record<ToolStatus, { label: string; cls: string }> = {
    dev: { label: '开发中', cls: 'bg-amber-50 text-amber-700' },
    live: { label: '已上线', cls: 'cap-on' },
};

function ToolDetail() {
    const { toolKey } = useParams<{ toolKey: string }>();
    const history = useHistory();
    const [tool, setTool] = useState<Tool | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const reload = useCallback(() => {
        if (!toolKey) return;
        setLoading(true);
        getTool(toolKey)
            .then((t) => {
                setTool(t);
                setError('');
            })
            .catch((e) => {
                const msg = e instanceof ApiError ? `${e.code}: ${e.message}` : String(e);
                setError(msg);
            })
            .finally(() => setLoading(false));
    }, [toolKey]);

    useEffect(() => {
        reload();
    }, [reload]);

    if (loading) {
        return <div className="text-sm text-slate-500">加载工具…</div>;
    }
    if (error || !tool) {
        return (
            <div className="space-y-4">
                <Link to="/tools" className="text-sm text-brand-600 hover:underline">
                    ← 返回工具市场
                </Link>
                <div className="card p-4 text-sm text-rose-600 border-rose-200 bg-rose-50">
                    {error || '工具不存在'}
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-5">
            <button
                type="button"
                className="text-sm text-brand-600 hover:underline"
                onClick={() => history.push('/tools')}
            >
                ← 返回工具市场
            </button>
            <BoundDevices tool={tool} onChanged={reload} />
            <ToolBody tool={tool} />
        </div>
    );
}

// BoundDevices 关联设备卡片: 绑定了本换算服务的设备清单 (device.convert_tool_key 反查).
// 可在此直接添加/移除绑定 (改写设备的 convert_tool_key, 与设备工况页顶部「换算服务」同一份数据);
// 统一走审批工单. 可换算范围按规则判定 (同工艺架构 + 同光源).
function BoundDevices({ tool, onChanged }: { tool: Tool; onChanged: () => void }) {
    const canUpdate = useCan('tool', 'update');
    const bound = tool.boundDevices || [];
    const [allDevices, setAllDevices] = useState<Device[] | null>(null);
    const [adding, setAdding] = useState(false);
    const [selKey, setSelKey] = useState('');
    const [busyKey, setBusyKey] = useState(''); // 正在写入的 deviceKey ('+add' = 添加中)

    useEffect(() => {
        if (!adding || allDevices !== null) return;
        listDevices({ pageSize: 100 })
            .then((r) => setAllDevices(r.list || []))
            .catch(() => setAllDevices([]));
    }, [adding, allDevices]);

    const bind = async (deviceKey: string, toolKey: string) => {
        setBusyKey(toolKey ? '+add' : deviceKey);
        try {
            const res = await updateDevice(deviceKey, { convertToolKey: toolKey });
            if (!alertApprovalIfPending(res.message)) {
                onChanged();
                setSelKey('');
                setAdding(false);
            }
        } catch (e) {
            const msg = e instanceof ApiError ? `${e.code}: ${e.message}` : String(e);
            alertDialog(`操作失败: ${msg}`);
        } finally {
            setBusyKey('');
        }
    };

    const boundKeys = new Set(bound.map((d) => d.deviceKey));
    const candidates = (allDevices || []).filter((d) => !boundKeys.has(d.deviceKey));
    const selected = candidates.find((d) => d.deviceKey === selKey);

    return (
        <div className="card p-4 space-y-2">
            <div className="flex items-center justify-between">
                <div className="text-sm font-semibold">关联设备 ({bound.length})</div>
                {!adding && (
                    <button
                        type="button"
                        className="text-xs text-brand-600 hover:underline"
                        onClick={() => setAdding(true)} hidden={!canUpdate}>
                        ＋ 添加设备
                    </button>
                )}
            </div>
            {bound.length === 0 ? (
                <div className="text-xs text-slate-400">暂无设备绑定本服务</div>
            ) : (
                <div className="flex flex-wrap gap-1.5">
                    {bound.map((d) => (
                        <span
                            key={d.deviceKey}
                            className="tag bg-slate-100 text-slate-600 inline-flex items-center gap-1"
                            title={d.deviceKey}
                        >
                            {d.name || d.deviceKey}
                            <span className="font-mono text-[10px] text-slate-400">{d.deviceKey}</span>
                            <button
                                type="button"
                                className="text-slate-400 hover:text-rose-500 disabled:opacity-50"
                                title="解除关联 (清空该设备的换算服务绑定)"
                                disabled={busyKey === d.deviceKey}
                                onClick={async () => {
                                    if (!await confirmDialog(`确认解除 ${d.deviceKey} 与本服务的关联?`)) return;
                                    bind(d.deviceKey, '');
                                }}
                            >
                                ✕
                            </button>
                        </span>
                    ))}
                </div>
            )}
            {adding && (
                <div className="flex items-center gap-2 flex-wrap pt-1">
                    <SearchSelect
                        className="w-80 text-sm"
                        value={selKey}
                        onChange={(v) => setSelKey(v)}
                        disabled={busyKey === '+add'}
                        placeholder={allDevices === null ? '加载设备中…' : '选择要关联的设备'}
                        options={candidates.map((d) => ({
                            value: d.deviceKey,
                            label: `${deviceLabel(d)} · ${d.deviceKey}${
                                d.convertToolKey ? ` (已绑 ${d.convertToolKey}, 将改绑)` : ''
                            }`,
                        }))}
                    />
                    <button
                        type="button"
                        className="btn-primary text-xs px-2 py-1"
                        disabled={!selKey || busyKey === '+add'}
                        onClick={async () => {
                            if (!selected) return;
                            if (
                                selected.convertToolKey &&
                                selected.convertToolKey !== tool.toolKey &&
                                !await confirmDialog(
                                    `${selected.deviceKey} 已绑定 ${selected.convertToolKey}, 确认改绑到 ${tool.toolKey}?`,
                                )
                            ) {
                                return;
                            }
                            bind(selected.deviceKey, tool.toolKey);
                        }}
                    >
                        {busyKey === '+add' ? '关联中…' : '关联'}
                    </button>
                    <button
                        type="button"
                        className="btn-ghost text-xs px-2 py-1"
                        onClick={() => {
                            setAdding(false);
                            setSelKey('');
                        }}
                        disabled={busyKey === '+add'}
                    >
                        取消
                    </button>
                </div>
            )}
            <div className="text-xs text-slate-400">
                绑定即改写设备的换算服务 (与设备工况页顶部「换算服务」同一份数据); 可换算范围按规则判定:
                工艺架构 + 光源相同即可互换算。
            </div>
        </div>
    );
}

function ToolBody({ tool }: { tool: Tool }) {
    const tag = STATUS_TAG[tool.status];

    if (tool.toolKey === 'power-calc') {
        return (
            <div className="card p-4 space-y-4">
                <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded bg-slate-100 grid place-items-center text-2xl">
                        {tool.icon || '⚡'}
                    </div>
                    <div>
                        <div className="text-base font-semibold flex items-center gap-2">
                            {tool.name}
                            <span className={`tag ${tag.cls}`}>{tag.label}</span>
                        </div>
                        <div className="text-xs text-slate-500">{tool.description}</div>
                    </div>
                </div>
                <PowerCalcWidget />
            </div>
        );
    }

    if (tool.toolKey === 'param-rec') {
        return <ParamRecPlaceholder tool={tool} />;
    }

    // 通用占位: 显示工具元信息 + endpoint 调用示例
    return (
        <div className="card p-6 space-y-4">
            <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded bg-slate-100 grid place-items-center text-2xl">
                    {tool.icon || '🛠️'}
                </div>
                <div>
                    <div className="text-base font-semibold flex items-center gap-2">
                        {tool.name}
                        <span className={`tag ${tag.cls}`}>{tag.label}</span>
                    </div>
                    <div className="text-xs text-slate-500">{tool.description}</div>
                </div>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="bg-slate-50 rounded p-3">
                    <div className="text-xs text-slate-400">服务标识</div>
                    <div className="font-mono">{tool.svcLabel || tool.toolKey}</div>
                </div>
                <div className="bg-slate-50 rounded p-3">
                    <div className="text-xs text-slate-400">接入方式</div>
                    <div>{tool.isInternal ? '内置' : '外部'} · 统一 Tool API</div>
                </div>
            </div>
            {tool.endpoint && (
                <div className="bg-slate-900 text-slate-100 rounded p-3 text-xs font-mono">
                    POST {tool.endpoint}
                    {tool.inputSchema && (
                        <pre className="text-slate-400 mt-1 whitespace-pre-wrap">
                            {buildInputTemplate(tool.inputSchema)}
                        </pre>
                    )}
                </div>
            )}
            <div className="bg-slate-900 text-slate-100 rounded p-3 text-xs font-mono">
                内部: POST /v1/tools/{tool.toolKey}/invoke
                <br />
                第三方: POST /openapi/v1/tools/invoke{' '}
                <span className="text-slate-400">{`{ "toolKey": "${tool.toolKey}", "input": {...} }`}</span>
            </div>
            <ToolTryInvoke tool={tool} />
            <div className="text-xs text-slate-500">
                该服务以独立后端形式部署, 平台按注册表透传调用 (不解释工具协议);
                live 状态对第三方开放 (Bearer mlk_ token), dev 状态仅管理员可在此灰度试跑.
            </div>
        </div>
    );
}

// buildInputTemplate 从注册的 inputSchema 生成试跑 JSON 模板.
// 落库格式是 JSON 对象 (可嵌套, 值为「类型, 说明」提示文本) — 原样美化展示,
// 类型说明即占位符, 调用时替换成真实值; 老数据兼容逗号清单.
function buildInputTemplate(inputSchema?: string): string {
    if (!inputSchema) return '{}';
    try {
        const obj = JSON.parse(inputSchema);
        if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
            return JSON.stringify(obj, null, 2);
        }
    } catch {
        const keys = inputSchema
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean);
        if (keys.length > 0) {
            const tpl: Record<string, string> = {};
            keys.forEach((k) => {
                tpl[k] = '';
            });
            return JSON.stringify(tpl, null, 2);
        }
    }
    return '{}';
}

// ToolTryInvoke 统一 Tool API 试跑: JSON 入参 → 平台透传 → 展示工具响应.
function ToolTryInvoke({ tool }: { tool: Tool }) {
    const [input, setInput] = useState(() => buildInputTemplate(tool.inputSchema));
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');
    const [outcome, setOutcome] = useState<InvokeOutcome | null>(null);

    const onInvoke = async () => {
        let parsed: unknown;
        try {
            parsed = JSON.parse(input || '{}');
        } catch (e) {
            setErr(`入参不是合法 JSON: ${String(e)}`);
            return;
        }
        setBusy(true);
        setErr('');
        setOutcome(null);
        try {
            setOutcome(await invokeTool(tool.toolKey, parsed));
        } catch (e) {
            setErr(e instanceof ApiError ? `${e.code}: ${e.message}` : String(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="space-y-2">
            <div className="text-sm font-semibold">试跑 (统一 Tool API)</div>
            <textarea
                className="inp font-mono text-xs"
                style={{ height: 140, padding: 8, resize: 'vertical' }}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                spellCheck={false}
            />
            <div className="flex items-center gap-3">
                <button type="button" className="btn-primary" onClick={onInvoke} disabled={busy}>
                    {busy ? '调用中…' : '调用'}
                </button>
                {tool.status === 'dev' && (
                    <span className="text-xs text-amber-600">dev 状态: 仅管理员可试跑, 上线后对第三方开放</span>
                )}
            </div>
            {err && (
                <div className="card p-3 text-sm text-rose-600 border-rose-200 bg-rose-50">{err}</div>
            )}
            {outcome && (
                <div className="card p-3 text-xs space-y-1">
                    <div>
                        工具 HTTP <span className="font-mono">{outcome.httpStatus}</span> ·{' '}
                        <span className="font-mono">{outcome.elapsedMs}ms</span>
                    </div>
                    <pre className="font-mono bg-slate-50 p-2 rounded overflow-auto max-h-64">
                        {JSON.stringify(outcome.result, null, 2)}
                    </pre>
                </div>
            )}
        </div>
    );
}

function ParamRecPlaceholder({ tool }: { tool: Tool }) {
    // 参数推荐工具二级页: 求解在工具服务侧, 平台经统一 Tool API 透传试跑.
    const tag = STATUS_TAG[tool.status];

    return (
        <div className="space-y-4">
            <div className="card p-4 space-y-3">
                <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded bg-slate-100 grid place-items-center text-2xl">
                        {tool.icon || '🎯'}
                    </div>
                    <div>
                        <div className="text-base font-semibold flex items-center gap-2">
                            {tool.name}
                            <span className={`tag ${tag.cls}`}>{tag.label}</span>
                        </div>
                        <div className="text-xs text-slate-500">
                            {tool.description || '基于材料 × 设备 × 模式 求解推荐参数, 与经验值互验'}
                        </div>
                    </div>
                </div>
                <ToolTryInvoke tool={tool} />
            </div>
            <div className="card p-4 text-xs text-slate-500 leading-relaxed">
                <div className="font-semibold text-slate-700 mb-1.5">接入说明</div>
                平台按注册表把入参透传到工具 endpoint (统一 Tool API §7.8),
                入参/出参协议以工具注册的 inputSchema/outputSchema 为准.
            </div>
        </div>
    );
}

export default ToolDetail;
